import { Card, SerializableCard } from "./Card";
import { StackCard } from "./StackCard";
import { StackCardList } from "./StackCardList";

export class Deck {
    /**
     * 推しカード
     */
    public readonly favoriteCards: Card[];

    /**
     * 通常カード
     */
    public readonly normalCards: StackCard[];

    constructor(favoriteCards: Card[] = [], normalCards: StackCard[] = []) {
        this.favoriteCards = favoriteCards;
        this.normalCards = normalCards;
    }

    public extractProductIds() {
        const favs = this.favoriteCards.map(card => card.cardProductId);
        const cards = this.normalCards.map(stackCard => stackCard.card.cardProductId);

        return [...favs].concat(cards).filter((value, index, self) => {
            return self.indexOf(value) === index;
        });
    }

    get totalCardCount() {
        let normalCard: number = 0;
        this.normalCards.forEach(value => { normalCard += value.count });

        return this.favoriteCards.length + normalCard;
    }

    public convertToSerializableDeck() {
        const serializable: SerializableDeck = {
            favoriteCards: this.favoriteCards.map(card => card.convertToSerializableCard()),
            normalCards: this.normalCards.map(stackCard => stackCard.convertToSerializableCard()).flat()
        };
        return serializable;
    }

    static loadFromSerializableDeck(deck: SerializableDeck) {
        const favoriteCards = deck.favoriteCards.map(value => Card.loadFromSerializableCard(value));
        const mergingCards: { [index: string]: StackCard } = {};
        deck.normalCards.forEach(value => {
            const key = `${value.cardNo}**${value.cardProductId}`;
            if (mergingCards[key]) {
                mergingCards[key].count++;
            } else {
                mergingCards[key] = new StackCard(Card.loadFromSerializableCard(value), 1);
            }
        });

        const newDeck = new Deck(
            favoriteCards,
            Object.values(mergingCards)
        );
        newDeck.sort();
        return newDeck;
    }

    public sort() {
        this.favoriteCards.sort((a, b) => {
            if (a.isEqual(b)) {
                return 0;
            }

            if (a.cardProductId > b.cardProductId
                || (a.cardProductId === b.cardProductId && b.cardNo.localeCompare(a.cardNo, 'ja') <= 0)) {
                return 1;
            }
            return -1;
        });
    }

    static filterDuplicateStackCards(stackCards: StackCard[]) {
        return stackCards.filter((stackCard, index) => {
            const duplicateCard = stackCards.slice(0, index).find((searchValue) => searchValue.card.isEqual(stackCard.card));
            if (duplicateCard) {
                console.warn("Duplicate card found: ", index);
            }
            return !duplicateCard;
        });
    }

    static insertStackCardOrdered(array: StackCard[], card: Card, countOfIfNewStack: number) {
        //If Already exists card then count up.
        const existsCard = array.find((stackCard) => stackCard.card.isEqual(card));
        if (existsCard) {
            existsCard.count++;
            return;
        }

        //Scanning insert position.
        let isInserted = false;
        for (let i = 0; i < array.length; i++) {
            const element = array[i];
            const compareCard = element.card;
            if (compareCard.cardProductId > card.cardProductId
                || (compareCard.cardProductId === card.cardProductId && card.cardNo.localeCompare(compareCard.cardNo, 'ja') <= 0)) {
                array.splice(i, 0, new StackCard(card, countOfIfNewStack));
                isInserted = true;
                break;
            }
        }

        //If can't find insert position then insert last.
        if (!isInserted) {
            array.push(new StackCard(card, countOfIfNewStack));
            isInserted = true;
        }
    }

    static insertCardOrdered(array: Card[], card: Card) {
        //Scanning insert position.
        let isInserted = false;
        for (let i = 0; i < array.length; i++) {
            const compareCard = array[i];
            if (compareCard.cardProductId > card.cardProductId
                || (compareCard.cardProductId === card.cardProductId && card.cardNo.localeCompare(compareCard.cardNo, 'ja') <= 0)) {
                array.splice(i, 0, card);
                isInserted = true;
                break;
            }
        }

        //If can't find insert position then insert last.
        if (!isInserted) {
            array.push(card);
            isInserted = true;
        }
    }

    public aggregateCards() {
        const list = new StackCardList(this.normalCards);
        this.favoriteCards.forEach(card => {
            list.incrementOrAddCard(card, 1);
        });
        return list;
    }
}

export interface SerializableDeck {
    /**
     * 推しカード
     */
    favoriteCards: SerializableCard[];

    /**
     * 通常カード
     */
    normalCards: SerializableCard[];
}